import { router } from '@inertiajs/react'
import { useState } from 'react'
import { MessageSquareIcon, PlusIcon, UsersIcon } from 'lucide-react'

import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { AppHeader } from '@/components/app-header'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { NewConversationDialog } from '@/components/new-conversation-dialog'

interface User {
  id: number
  name: string
  email: string
}

interface Message {
  id: number
  content: string
  user: User
  created_at: string
}

interface Conversation {
  id: number
  type: 'direct_message' | 'channel'
  name: string
  description?: string
  is_private: boolean
  last_message_at?: string
  unread_count: number
  members: User[]
  last_message?: Message
}

interface MessagesIndexProps {
  conversations: Conversation[]
  current_user: User
  total_unread: number
  available_users: User[]
}

export default function MessagesIndex({ conversations, current_user, total_unread, available_users }: MessagesIndexProps) {
  const [selectedType, setSelectedType] = useState<'all' | 'direct_message' | 'channel'>('all')
  const [dialogOpen, setDialogOpen] = useState(false)

  const filteredConversations = conversations.filter(conv => {
    if (selectedType === 'all') return true
    return conv.type === selectedType
  })

  const handleConversationClick = (conversationId: number) => {
    router.visit(`/messages/conversations/${conversationId}`)
  }

  const handleNewMessage = () => {
    setDialogOpen(true)
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const formatTimestamp = (timestamp?: string) => {
    if (!timestamp) return ''
    const date = new Date(timestamp)
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    })
  }

  return (
    <>
      <AppHeader breadcrumbs={[{ label: 'Messages' }]} />

      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            <div className="px-4 lg:px-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex gap-2">
                  <Button
                    variant={selectedType === 'all' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedType('all')}
                  >
                    All
                    {total_unread > 0 && (
                      <Badge variant="secondary" className="ml-2">
                        {total_unread}
                      </Badge>
                    )}
                  </Button>
                  <Button
                    variant={selectedType === 'direct_message' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedType('direct_message')}
                  >
                    <MessageSquareIcon className="h-4 w-4 mr-2" />
                    Direct Messages
                  </Button>
                  <Button
                    variant={selectedType === 'channel' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedType('channel')}
                  >
                    <UsersIcon className="h-4 w-4 mr-2" />
                    Channels
                  </Button>
                </div>
                <Button onClick={handleNewMessage}>
                  <PlusIcon className="h-4 w-4 mr-2" />
                  New Message
                </Button>
              </div>

              {filteredConversations.length === 0 ? (
                <Card className="from-primary/5 to-card bg-gradient-to-t shadow-xs">
                  <CardContent className="py-12 text-center">
                    <MessageSquareIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No conversations yet</h3>
                    <p className="text-muted-foreground mb-4">
                      Start a conversation with your colleagues
                    </p>
                    <Button onClick={handleNewMessage}>
                      <PlusIcon className="h-4 w-4 mr-2" />
                      Start Messaging
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-2">
                  {filteredConversations.map((conversation) => (
                    <Card
                      key={conversation.id}
                      className="cursor-pointer hover:border-primary/50 hover:bg-primary/5 transition-all border-l-4 border-l-transparent hover:border-l-primary"
                      onClick={() => handleConversationClick(conversation.id)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <Avatar>
                            <AvatarFallback>
                              {conversation.type === 'channel'
                                ? <UsersIcon className="h-5 w-5" />
                                : getInitials(conversation.name)
                              }
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <h4 className="font-semibold truncate">
                                {conversation.type === 'channel' && '#'}{conversation.name}
                              </h4>
                              <span className="text-xs text-muted-foreground ml-2">
                                {formatTimestamp(conversation.last_message_at)}
                              </span>
                            </div>
                            {conversation.last_message && (
                              <p className="text-sm text-muted-foreground truncate">
                                <span className="font-medium">
                                  {conversation.last_message.user.name}:
                                </span>{' '}
                                {conversation.last_message.content}
                              </p>
                            )}
                            {conversation.description && !conversation.last_message && (
                              <p className="text-sm text-muted-foreground truncate">
                                {conversation.description}
                              </p>
                            )}
                          </div>
                          {conversation.unread_count > 0 && (
                            <div className="ml-2 flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-primary text-primary-foreground text-xs font-semibold">
                              {conversation.unread_count}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <NewConversationDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        availableUsers={available_users}
        currentUser={current_user}
      />
    </>
  )
}
